import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { api, type Registry } from "@/api.js"
import { type ConfigChange, describeChanges } from "./diff.js"
import {
  type EditorCountry,
  type EditorProviders,
  engineOf,
  fromDocument,
  providersOf,
  toDocument,
} from "./model.js"

/**
 * One draft of the configuration document, shared by the two screens that edit
 * it: Konfiguracja edits the rules of one country, Ustawienia edits which
 * countries there are and the policy for the ones without a set.
 *
 * **Both publish the whole document**, because that is what a version is. Each
 * screen seeds its draft from what is stored, so publishing from one carries
 * the other's stored settings through untouched.
 *
 * The two screens are what made the collision below worth handling, but they
 * did not create it: the editor already reseeded itself whenever the query
 * refetched, so a window regaining focus while somebody was mid-edit dropped
 * their work without a word.
 */
export interface Draft {
  countries: EditorCountry[]
  setCountries: (next: EditorCountry[]) => void
  /**
   * What each source is set to.
   *
   * **Held here for the same reason the rules are**: publishing rebuilds the
   * whole document from this draft, so a section it does not carry is a
   * section the next publish deletes. Provider settings were parsed by nothing
   * until 2026-09-24, so there was nothing to lose; the moment one became
   * settable, leaving it out would have been the `params` bug one level up.
   */
  providers: EditorProviders
  setProviders: (next: EditorProviders) => void
  policy: string
  setPolicy: (next: string) => void
  /** What publishing would change, measured against what is live right now. */
  changes: readonly ConfigChange[]
  /** The version that landed under this draft while it was being edited. */
  supersededBy: number | null
  /** Throw the draft away and take what is live. */
  discard: () => void
  note: string
  setNote: (next: string) => void
  publish: () => void
  publishing: boolean
  error: unknown
}

export function useDraft(
  document: Record<string, unknown>,
  registry: Registry,
  version: number | null,
): Draft {
  const client = useQueryClient()
  const [basis, setBasis] = useState({ document, version })
  const [countries, setCountries] = useState(() => fromDocument(document, registry))
  const [providers, setProviders] = useState(() => providersOf(document, registry))
  const [policy, setPolicy] = useState(() => engineOf(document).uncoveredCountryPolicy)
  const [note, setNote] = useState("")

  // Carried through untouched: the seed a new country starts from, not a
  // setting anybody adjusts.
  const defaultThreshold = engineOf(basis.document).blockThreshold

  const draft = useMemo(
    () => toDocument(countries, defaultThreshold, policy, registry, providers),
    [countries, defaultThreshold, policy, registry, providers],
  )

  /*
   * Two comparisons, answering different questions. Against the document this
   * draft was seeded from: did *this person* change anything. Against what is
   * live: what would publishing do. They are the same until somebody else
   * publishes, and telling them apart is the whole of the handling below.
   */
  const edited = useMemo(
    () => describeChanges(basis.document, draft, registry).length > 0,
    [basis.document, draft, registry],
  )
  const changes = useMemo(
    () => describeChanges(document, draft, registry),
    [document, draft, registry],
  )

  const editedRef = useRef(edited)
  editedRef.current = edited

  useEffect(() => {
    // A refetch reseeds an untouched draft and leaves a touched one alone.
    if (editedRef.current) return
    setBasis({ document, version })
    setCountries(fromDocument(document, registry))
    setProviders(providersOf(document, registry))
    setPolicy(engineOf(document).uncoveredCountryPolicy)
  }, [document, version, registry])

  const publish = useMutation({
    mutationFn: () => api.publishConfig(draft, note.trim() === "" ? null : note.trim()),
    onSuccess: (result) => {
      setNote("")
      // The draft becomes the new basis, so our own publish is not read back as
      // somebody else's version landing underneath us.
      setBasis({ document: draft, version: result.version })
      void client.invalidateQueries({ queryKey: ["config"] })
    },
  })

  return {
    countries,
    setCountries,
    providers,
    setProviders,
    policy,
    setPolicy,
    changes,
    supersededBy: document !== basis.document && edited ? version : null,
    discard: () => {
      setBasis({ document, version })
      setCountries(fromDocument(document, registry))
      setProviders(providersOf(document, registry))
      setPolicy(engineOf(document).uncoveredCountryPolicy)
    },
    note,
    setNote,
    publish: () => publish.mutate(),
    publishing: publish.isPending,
    error: publish.error,
  }
}
